"use client";

import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

interface ZipCityFieldsProps {
  zipCode: string;
  city: string;
  cityOptions?: string[];
  showCityInput: boolean;
  isFetchingCity: boolean;
  errors: { zipCode: string; city: string };
  disabled?: boolean;
  onZipCodeChange: (v: string) => void;
  onCityChange: (v: string) => void;
  zipCodeRef: React.RefObject<HTMLInputElement>;
  cityRef: React.RefObject<HTMLDivElement>;
}

const ZipCityFields: React.FC<ZipCityFieldsProps> = ({
  zipCode,
  city,
  cityOptions = [],
  showCityInput,
  isFetchingCity,
  errors,
  disabled = false,
  onZipCodeChange,
  onCityChange,
  zipCodeRef,
  cityRef,
}) => {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-1 gap-4">
      <div className="space-y-2 field-row">
        <Label htmlFor="zipCode">{t("contactDetails.zipCode")}</Label>
        <Input
          id="zipCode"
          name="zipCode"
          autoComplete="postal-code"
          type="text"
          value={zipCode}
          onChange={(e) => onZipCodeChange(e.target.value)}
          className={cn(errors.zipCode && "border-destructive focus-visible:ring-destructive")}
          disabled={disabled || isFetchingCity}
          ref={zipCodeRef}
          maxLength={4}
        />
        {isFetchingCity && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        {errors.zipCode && <p className="text-sm text-destructive">{errors.zipCode}</p>}
      </div>
      {showCityInput && (
        <div className="space-y-2 field-row" ref={cityRef}>
          <Label htmlFor="city">{t("contactDetails.city")}</Label>
          {cityOptions.length > 1 ? (
            <Select name="city" autoComplete="address-level2" value={city} onValueChange={onCityChange} disabled={disabled || isFetchingCity}>
              <SelectTrigger
                id="city"
                className={cn(errors.city && "border-destructive focus-visible:ring-destructive")}
              >
                <SelectValue placeholder={t("contactDetails.selectCityPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {cityOptions.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              id="city"
              name="city"
              autoComplete="address-level2"
              type="text"
              value={city}
              onChange={(e) => onCityChange(e.target.value)}
              className={cn(errors.city && "border-destructive focus-visible:ring-destructive")}
              disabled={disabled || isFetchingCity}
            />
          )}
          {errors.city && <p className="text-sm text-destructive">{errors.city}</p>}
        </div>
      )}
    </div>
  );
};

export default ZipCityFields;
